/* ==========================================================================
   LSC - Product of the Day
   Browser half of sections/lsc-product-of-the-day.liquid.

   Modes (set by Liquid in the section's config JSON):

     "pinned"  -> admin pinned a product. Nothing to do.
     "server"  -> the pool fits in one Liquid page (<=250 products) and Liquid
                  made the complete pick. We only step in if the HTML is from
                  an earlier day (Shopify serves cached pages, and a cached
                  page keeps the date it was rendered on).
     "hybrid"  -> the pool spans several pages. Liquid scored PAGE 1, rendered
                  that winner and passed its score ("score"). We fetch only
                  pages 2..N and swap the card if one of them scores higher.
                  (LSC - added after the 25 Sep 2026 audit; replaces the old
                  "client" mode that fetched every page and always swapped.)

   The pick (identical to the Liquid version, by design):

     score(product) = sha256("lsc-potd|<shuffle key>|<YYYY-MM-DD>|<product id>")
     winner         = eligible product with the highest score (hex string order)

   eligible = available AND (has quote, if required) AND published before today.
   The eligibility flags come from snippets/lsc-product-of-the-day-flags.liquid
   via the data section, so there is one definition shared by both halves.

   Speed-ups that run before this file (snippets/lsc-product-of-the-day-boot.liquid):
     - the pool requests are started at parse time (window.LscPotdBoot);
     - a visitor's cached pick for today is painted before first render.
   This file works without them too (theme editor re-renders skip inline JS).

   Per-visitor cache (localStorage, key
   "lsc-potd:v2:<pool>|<salt>|<requireQuote>|<imageSource>|<detailKey>"):
     { day, cur, handle, card, quote, detail, hasQuote, t }
     card/quote hold the fetched fragment markup; they are null when the pick
     is the card Liquid renders anyway. "cur" is "<currency>|<locale>" because
     prices are baked into the markup. Refreshed in the background after
     REVALIDATE_MS so prices/stock never go more than that out of date.
     imageSource is in the key because the stored card already carries the
     chosen photo; changing the "Card image" setting must not reuse it.
     detailKey likewise (LSC - 2 Oct 2026): the stored detail line is the
     field the section asked for.

   Product detail line (LSC - 2 Oct 2026): a product field shown above the
   quote, custom.blurb by default. Liquid renders it for the cards it picks;
   for cards fetched here, the data section sends every field in
   data-details and fetchFragment() takes the one named by cfg.detailKey.

   Card image (LSC - 26 Sep 2026): the section can show the main photo or one
   of the custom.image_1..3 metafield photos. Liquid swaps the photo in the
   cards it renders; for cards fetched here, applyImageChoice() does the same
   swap using the base URLs the data section lists in data-images.

   Debugging:
     ?lsc_potd=client  ignore caches and the bootstrap, redo the draw from page 1,
                       and log parity checks against Liquid's page-1 pick.
     ?lsc_potd=debug   log what the script decided, without forcing anything.
   ========================================================================== */
(() => {
  'use strict';

  // Guard against the script being included twice (e.g. two sections).
  if (window.LscPotd) return;

  const CACHE_PREFIX = 'lsc-potd:v2:'; // v2: entries carry card markup (keep in sync with the bootstrap)
  const REVALIDATE_MS = 30 * 60 * 1000; // refresh a cached card's markup after 30 minutes
  const params = new URLSearchParams(window.location.search);
  const FORCE_CLIENT = params.get('lsc_potd') === 'client';
  const DEBUG = params.has('lsc_potd');

  /** Console output only when ?lsc_potd is present. */
  const log = (...args) => {
    if (DEBUG) console.info('[lsc-potd]', ...args);
  };

  /* ------------------------------------------------------------------------
     Dates
     ------------------------------------------------------------------------ */

  /**
   * Today's date in the SHOP's timezone as "YYYY-MM-DD".
   * Liquid hands us the shop's UTC offset (e.g. "-0400"); we shift "now" by
   * it and read the UTC calendar date. (Across a DST switch a cached page
   * carries the old offset, which can only matter within an hour of
   * midnight on the two switch days.) Keep in sync with the bootstrap.
   */
  function shopToday(utcOffset) {
    const match = /^([+-])(\d{2}):?(\d{2})$/.exec(String(utcOffset || '').trim());
    let minutes = 0;
    if (match) {
      minutes = (match[1] === '-' ? -1 : 1) * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
    }
    return new Date(Date.now() + minutes * 60000).toISOString().slice(0, 10);
  }

  /** "2026-09-25" -> "Friday, September 25" (matches Liquid's '%A, %B %-d'). */
  function formatDay(dayKey) {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${dayKey}T12:00:00Z`));
  }

  /* ------------------------------------------------------------------------
     Hashing - SHA-256 hex, same bytes as Liquid's | sha256 (UTF-8 input)
     ------------------------------------------------------------------------ */

  async function sha256Hex(text) {
    const bytes = new TextEncoder().encode(text);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  }

  /** The exact seed string Liquid builds. Change both or neither. */
  const seedFor = (cfg, dayKey, productId) => `lsc-potd|${cfg.salt}|${dayKey}|${productId}`;

  /* ------------------------------------------------------------------------
     Eligibility - mirrors the checks in the Liquid section
     flags = [available (0|1), has_quote (0|1), published YYYYMMDD]
     ------------------------------------------------------------------------ */

  function isEligible(flags, cfg, dayKey) {
    const [available, hasQuote, published] = flags.map(Number);
    const todayInt = Number(dayKey.replace(/-/g, ''));
    if (available !== 1) return false;
    if (cfg.requireQuote && hasQuote !== 1) return false;
    // Products published today join tomorrow, so adding products mid-day
    // can never replace today's pick.
    if (published > 0 && published >= todayInt) return false;
    return true;
  }

  /* ------------------------------------------------------------------------
     Section Rendering API helpers
     ------------------------------------------------------------------------ */

  async function fetchText(url) {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`${res.status} for ${url}`);
    return res.text();
  }

  const parseDoc = (html) => new DOMParser().parseFromString(html, 'text/html');

  /** Pool page URL. Keep in sync with the bootstrap. */
  function poolUrlFor(cfg, page) {
    const sep = cfg.poolUrl.includes('?') ? '&' : '?';
    return `${cfg.poolUrl}${sep}section_id=${cfg.dataSection}&page=${page}`;
  }

  /** Data-section HTML -> { page, pages, products: [[id, handle, avail, quote, pub], ...] } */
  function parsePool(html) {
    const node = parseDoc(html).querySelector('script[data-lsc-potd-pool]');
    if (!node) throw new Error('No pool data in response');
    return JSON.parse(node.textContent);
  }

  // Instances on one page with the same pool share the same downloads.
  const poolRequests = new Map();

  /**
   * Every product row on pool pages from..N.
   * bootPages: optional text promises the bootstrap already started for
   * exactly those pages, so nothing is requested twice.
   */
  function loadPool(cfg, from, bootPages) {
    const key = `${cfg.poolUrl}|${from}`;
    if (poolRequests.has(key)) return poolRequests.get(key);

    const request = (async () => {
      const last = Math.max(from, Number(cfg.poolPages) || 1);
      const count = last - from + 1;
      const texts =
        bootPages && bootPages.length === count
          ? bootPages
          : Array.from({ length: count }, (_, i) => fetchText(poolUrlFor(cfg, from + i)));
      const pages = (await Promise.all(texts)).map(parsePool);

      // If the catalogue grew since the page was rendered, fetch the extras.
      const actual = Math.max(last, ...pages.map((pg) => Number(pg.pages) || 0));
      for (let p = last + 1; p <= actual; p += 1) {
        pages.push(parsePool(await fetchText(poolUrlFor(cfg, p))));
      }

      // Flatten and de-duplicate by product id (a product can shift pages if
      // the catalogue changes between two requests).
      const seen = new Set();
      const rows = [];
      pages.forEach((page) => {
        (page.products || []).forEach((row) => {
          if (!seen.has(row[0])) {
            seen.add(row[0]);
            rows.push(row);
          }
        });
      });
      return rows;
    })();

    poolRequests.set(key, request);
    request.catch(() => poolRequests.delete(key)); // allow a retry after failure
    return request;
  }

  /**
   * Highest-scoring eligible product among rows, starting from an optional
   * baseline ({ handle, score } = Liquid's page-1 winner in hybrid mode).
   * Returns { handle, score }; handle is null when nothing qualifies.
   */
  async function bestOf(rows, cfg, dayKey, baseline) {
    let best = baseline && baseline.handle ? { handle: baseline.handle, score: baseline.score } : { handle: null, score: '' };
    const eligible = rows.filter((row) => isEligible(row.slice(2), cfg, dayKey));
    const scores = await Promise.all(eligible.map((row) => sha256Hex(seedFor(cfg, dayKey, row[0]))));
    scores.forEach((score, i) => {
      if (score > best.score) best = { handle: eligible[i][1], score };
    });
    log(`${rows.length} products scored (${eligible.length} eligible) on ${dayKey}; best: ${best.handle}`);
    return best;
  }

  /**
   * LSC - Swap the card's main photo for the section's chosen one.
   * Mirrors the Liquid swap in snippets/lsc-product-of-the-day-feature.liquid:
   * every src/srcset of the main photo is "<base>&width=N", so replacing the
   * base with the chosen photo's base swaps every size at once. The markup is
   * edited in an inert <template> so nothing loads or upgrades mid-edit.
   * Falls back to the untouched card (main photo) if anything is missing.
   */
  function applyImageChoice(cardHtml, imagesJson, source) {
    if (!source || source === 'main') return cardHtml;
    let images;
    try {
      images = JSON.parse(imagesJson || '{}');
    } catch (e) {
      return cardHtml;
    }
    const from = images.main;
    const to = images[source];
    if (!from || !to || !from.u || !to.u) return cardHtml; // no such photo: keep main

    const tpl = document.createElement('template');
    tpl.innerHTML = cardHtml;
    tpl.content.querySelectorAll('img').forEach((img) => {
      let swapped = false;
      ['src', 'srcset'].forEach((attr) => {
        const value = img.getAttribute(attr);
        if (value && value.includes(from.u)) {
          img.setAttribute(attr, value.split(from.u).join(to.u));
          swapped = true;
        }
      });
      if (swapped && to.a) img.setAttribute('alt', to.a);
    });
    return tpl.innerHTML;
  }

  /**
   * One product's card + quote markup, or null if the product is no longer
   * eligible (e.g. a pick cached earlier today has since sold out).
   * Returns { handle, card, quote, hasQuote }.
   */
  async function fetchFragment(cfg, handle, dayKey) {
    const html = await fetchText(`${cfg.productsUrl}${encodeURIComponent(handle)}?section_id=${cfg.dataSection}`);
    const fragment = parseDoc(html).querySelector('[data-lsc-potd-fragment]');
    if (!fragment) return null;
    const flags = (fragment.getAttribute('data-flags') || '').split(',');
    if (!isEligible(flags, cfg, dayKey)) return null;

    const card = fragment.querySelector('[data-lsc-potd-part="card"]');
    const quote = fragment.querySelector('[data-lsc-potd-part="quote"]');

    // LSC - the section's "Product detail" field, from the JSON of all fields.
    let detail = '';
    if (cfg.detailKey) {
      try {
        detail = JSON.parse(fragment.getAttribute('data-details') || '{}')[cfg.detailKey] || '';
      } catch (e) {
        detail = '';
      }
    }

    return {
      handle: fragment.getAttribute('data-handle') || handle,
      // LSC - apply the "Card image" choice (the endpoint always sends main).
      card: card ? applyImageChoice(card.innerHTML, fragment.getAttribute('data-images'), cfg.imageSource) : '',
      quote: quote ? quote.innerHTML : '',
      detail,
      hasQuote: !!(quote && quote.textContent.trim()),
    };
  }

  /* ------------------------------------------------------------------------
     Per-visitor cache
     ------------------------------------------------------------------------ */

  /** Keep in sync with the bootstrap. */
  const cacheKey = (cfg) =>
    `${CACHE_PREFIX}${cfg.poolUrl}|${cfg.salt}|${cfg.requireQuote ? 1 : 0}|${cfg.imageSource || 'main'}|${cfg.detailKey || ''}`;

  /** Today's entry for this pool/salt/currency, or null. */
  function readCache(cfg, dayKey) {
    try {
      const entry = JSON.parse(localStorage.getItem(cacheKey(cfg)) || 'null');
      return entry && entry.day === dayKey && entry.cur === cfg.currency && entry.handle ? entry : null;
    } catch (e) {
      return null; // private mode, blocked storage, bad JSON: just recompute
    }
  }

  /** parts: { handle, card, quote, hasQuote }; card null = "the server renders it". */
  function writeCache(cfg, dayKey, parts) {
    try {
      localStorage.setItem(
        cacheKey(cfg),
        JSON.stringify({
          day: dayKey,
          cur: cfg.currency,
          handle: parts.handle,
          card: parts.card || null,
          quote: parts.card ? parts.quote : null,
          detail: parts.card ? parts.detail || '' : null,
          hasQuote: !!parts.hasQuote,
          t: Date.now(),
        })
      );
    } catch (e) {
      /* storage unavailable or full; nothing to do */
    }
  }

  function clearCache(cfg) {
    try {
      localStorage.removeItem(cacheKey(cfg));
    } catch (e) {
      /* ignore */
    }
  }

  /* ------------------------------------------------------------------------
     DOM updates
     ------------------------------------------------------------------------ */

  /** Show today's date (a cached page may be showing yesterday's). */
  function updateDate(root, dayKey) {
    const time = root.querySelector('[data-lsc-potd-date]');
    if (!time || time.getAttribute('datetime') === dayKey) return;
    time.setAttribute('datetime', dayKey);
    time.textContent = formatDay(dayKey);
  }

  /** The handle of the card currently on screen ('' when it's the skeleton). */
  const shownHandle = (root) => root.getAttribute('data-handle') || '';

  /** Put a card + quote into the section. */
  function applyParts(root, parts) {
    const cardSlot = root.querySelector('[data-lsc-potd-slot="card"]');
    const quoteSlot = root.querySelector('[data-lsc-potd-slot="quote"]');

    // Theme custom elements (lazy-image, product-form, hover-button, ...)
    // upgrade automatically when parsed into the live document.
    if (cardSlot) cardSlot.innerHTML = parts.card;
    if (quoteSlot) quoteSlot.innerHTML = parts.quote || '';

    // LSC - product detail line; hide the block when the field is empty.
    const detailSlot = root.querySelector('[data-lsc-potd-slot="detail"]');
    if (detailSlot) detailSlot.innerHTML = parts.detail || '';
    root.classList.toggle('lsc-potd--no-detail', !parts.detail);

    root.classList.toggle('lsc-potd--no-quote', !parts.hasQuote);
    root.setAttribute('data-handle', parts.handle);
    root.setAttribute('data-swapped', '');
    root.dataset.state = 'ready';
  }

  /** Nothing qualifies: hide for shoppers, explain in the theme editor. */
  function showEmpty(root, cfg) {
    if (cfg.designMode) {
      const note = root.querySelector('[data-lsc-potd-empty]');
      if (note) note.hidden = false;
      root.querySelector('.lsc-potd__body')?.setAttribute('hidden', '');
      root.dataset.state = 'empty';
    } else {
      root.hidden = true;
    }
  }

  /* ------------------------------------------------------------------------
     Main
     ------------------------------------------------------------------------ */

  /**
   * Use today's cached pick if there is one. Returns true when the section is
   * settled; false means "no usable cache, run the draw".
   */
  async function useCache(root, cfg, today, serverCurrent) {
    const entry = readCache(cfg, today);
    if (!entry) return false;

    // Put the cached pick on screen if the bootstrap didn't already.
    if (shownHandle(root) !== entry.handle) {
      const parts = entry.card ? entry : await fetchFragment(cfg, entry.handle, today);
      if (!parts) {
        log(`cached pick ${entry.handle} no longer eligible`);
        clearCache(cfg);
        return false;
      }
      applyParts(root, parts);
      if (!entry.card) writeCache(cfg, today, parts);
      log(`cached pick ${entry.handle} applied`);
      if (!entry.card) return true; // just fetched fresh markup
    } else {
      root.dataset.state = 'ready';
    }

    // A card Liquid rendered on this very page view is fresh by definition.
    if (entry.handle === cfg.handle && serverCurrent) {
      log(`cached pick ${entry.handle} is the server card; nothing to fetch`);
      return true;
    }
    if (Date.now() - (entry.t || 0) < REVALIDATE_MS) {
      log(`cached pick ${entry.handle} is fresh`);
      return true;
    }

    // Older than REVALIDATE_MS: refresh the stored markup (prices, stock) in
    // the background. The DOM is left alone to avoid a visible re-render;
    // the next page view uses the refreshed copy.
    const fresh = await fetchFragment(cfg, entry.handle, today);
    if (fresh) {
      writeCache(cfg, today, fresh);
      log(`cached pick ${entry.handle} revalidated`);
      return true;
    }
    log(`cached pick ${entry.handle} no longer eligible, redrawing`);
    clearCache(cfg);
    return false;
  }

  /** ?lsc_potd=client: check Liquid's page-1 pick and the hybrid shortcut. */
  async function parityCheck(cfg, today, fullPick) {
    if (cfg.day !== today) {
      console.info(`[lsc-potd] parity check skipped: page rendered for ${cfg.day}, today is ${today}`);
      return;
    }
    // (a) Liquid's page-1 winner and score vs the browser scoring page 1 alone.
    const page1 = parsePool(await fetchText(poolUrlFor(cfg, 1))).products;
    const b1 = await bestOf(page1, cfg, today, null);
    const p1ok = b1.handle === (cfg.handle || null) && b1.score === (cfg.score || '');
    console.info(`[lsc-potd] parity (page 1): server=${cfg.handle} browser=${b1.handle}, scores ${b1.score === cfg.score ? 'equal' : 'differ'} -> ${p1ok ? 'MATCH' : 'DIFFERENT'}`);
    // (b) hybrid shortcut (page-1 baseline + pages 2..N) vs a full draw.
    if (cfg.mode === 'hybrid') {
      const rest = await loadPool(cfg, 2, null);
      const hybrid = await bestOf(rest, cfg, today, { handle: cfg.handle, score: cfg.score });
      console.info(`[lsc-potd] parity (hybrid vs full): hybrid=${hybrid.handle} full=${fullPick.handle} -> ${hybrid.handle === fullPick.handle ? 'MATCH' : 'DIFFERENT'}`);
    }
  }

  async function run(root) {
    if (root.dataset.lscPotdInit) return;
    root.dataset.lscPotdInit = '1';

    let cfg;
    try {
      cfg = JSON.parse(root.querySelector('script[data-lsc-potd-config]').textContent);
    } catch (e) {
      console.error('[lsc-potd] Missing or invalid config', e);
      return;
    }
    if (cfg.mode === 'pinned') {
      log('pinned product, nothing to do');
      return;
    }

    // Pick up the bootstrap's work (once; an editor re-render starts clean).
    const boot = (window.LscPotdBoot || {})[root.id] || null;
    if (window.LscPotdBoot) delete window.LscPotdBoot[root.id];

    const today = shopToday(cfg.utcOffset);
    const serverCurrent = cfg.day === today;
    updateDate(root, today);
    log(`mode=${cfg.mode}, server day=${cfg.day}, shop today=${today}, shown=${shownHandle(root) || '(skeleton)'}${boot && boot.injected ? ' [cached, pre-paint]' : ''}`);

    // Liquid's complete pick is current: nothing to do.
    if (cfg.mode === 'server' && serverCurrent && !FORCE_CLIENT) return;

    try {
      // 1. Today's cached pick, if any.
      if (!FORCE_CLIENT && (await useCache(root, cfg, today, serverCurrent))) return;

      // 2. The draw. In hybrid mode on the day Liquid rendered, page 1 is
      //    already scored: start from its winner and look at pages 2..N only.
      const from = cfg.mode === 'hybrid' && serverCurrent && !FORCE_CLIENT ? 2 : 1;
      const baseline = from === 2 ? { handle: cfg.handle, score: cfg.score } : null;
      const bootPages = boot && boot.from === from ? boot.pages : null;
      const rows = await loadPool(cfg, from, bootPages);
      const pick = await bestOf(rows, cfg, today, baseline);

      if (FORCE_CLIENT) await parityCheck(cfg, today, pick);

      if (!pick.handle) {
        if (!shownHandle(root)) showEmpty(root, cfg);
        return;
      }

      // Already on screen (the server card, usually): remember it, done.
      if (pick.handle === shownHandle(root)) {
        root.dataset.state = 'ready';
        writeCache(cfg, today, { handle: pick.handle, card: null, hasQuote: !root.classList.contains('lsc-potd--no-quote') });
        log(`pick ${pick.handle} is already shown`);
        return;
      }

      const parts = await fetchFragment(cfg, pick.handle, today);
      if (!parts) {
        // Pool and product page disagree (a copy mid-refresh). Keep whatever
        // is shown; with nothing shown, hide.
        if (!shownHandle(root)) showEmpty(root, cfg);
        return;
      }
      applyParts(root, parts);
      writeCache(cfg, today, parts);
      log(`swapped in ${pick.handle}`);
    } catch (error) {
      console.error('[lsc-potd]', error);
      // A server card (even a stale one) beats nothing; an empty skeleton doesn't.
      if (!shownHandle(root)) showEmpty(root, cfg);
    }
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-lsc-potd]').forEach((root) => run(root));
  }

  // Exposed for testing from the console.
  window.LscPotd = { init, bestOf, loadPool, parsePool, shopToday, sha256Hex, isEligible, applyImageChoice };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Theme editor re-renders a section when its settings change.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
